import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiBase } from '../hooks/useApiBase';
import {
  authFetch,
  clearAuthSessionToken,
  readLastAuthFetchRequest,
  readSessionToken,
  setAuthSessionToken,
} from '../lib/authFetch';
import type { AllowedPageValue } from '../types/generated/users-schema';

export type PresenceStatus = 'available' | 'busy' | 'offline';
type LoginRedirectReason = 'expired';

const AUTH_EXPIRED_EVENT = 'rh-auth-expired';
const LOGIN_REDIRECT_REASON_STORAGE_KEY = 'rh_login_redirect_reason';
const SESSION_STORAGE_KEY = 'rh_session_token';
const IS_PROD = Boolean(import.meta.env?.PROD);
const IS_TEST =
  (typeof import.meta !== 'undefined' && typeof import.meta.env?.MODE === 'string' && import.meta.env.MODE === 'test') ||
  (typeof process !== 'undefined' && typeof process.env?.NODE_ENV === 'string' && process.env.NODE_ENV === 'test');

export type AuthUser = {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  phone?: string;
  job_title?: string;
  role?: string;
  active?: boolean;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  external_id?: string;
  allowed_pages?: AllowedPageValue[];
  custom_fields?: Record<string, unknown>;
  [key: string]: unknown;
};

type AuthContextValue = {
  user: AuthUser | null;
  sessionToken: string;
  isAdmin: boolean;
  isBootstrapping: boolean;
  isAuthenticating: boolean;
  isProfileOpen: boolean;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  redirectToLogin: (targetPath?: string, options?: { reason?: LoginRedirectReason }) => void;
  updateUser: (updatedUser: AuthUser) => void;
  openProfile: () => void;
  closeProfile: () => void;
  getPresenceStatus: () => PresenceStatus;
  updatePresenceStatus: (status: PresenceStatus) => Promise<void>;
};

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const AUTH_USER_STORAGE_KEY = 'rh_auth_user';

function loadStoredUser(): AuthUser | null {
  if (typeof window === 'undefined' || !window?.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function persistUser(user: AuthUser | null) {
  if (typeof window === 'undefined' || !window?.localStorage) {
    return;
  }
  try {
    if (user) {
      window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    }
  } catch {
    // Ignore storage quota failures
  }
}

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const apiBase = useApiBase();
  const [user, setUser] = React.useState<AuthUser | null>(() => loadStoredUser());
  const [sessionToken, setSessionToken] = React.useState<string>(() => readSessionToken());
  const [isProfileOpen, setIsProfileOpen] = React.useState<boolean>(false);
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);
  const [isBootstrapping, setIsBootstrapping] = React.useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const authEpochRef = React.useRef(0);
  const logoutDebugCapturedRef = React.useRef(false);
  const lastAuthEventRef = React.useRef<
    | {
        ts: number;
        kind: 'login' | 'logout' | 'expired' | 'cross-tab';
        lastRequest: ReturnType<typeof readLastAuthFetchRequest>;
      }
    | null
  >(null);

  React.useEffect(() => {
    setIsBootstrapping(false);
  }, []);

  const bumpAuthEpoch = React.useCallback((kind: 'login' | 'logout' | 'expired' | 'cross-tab') => {
    authEpochRef.current += 1;
    lastAuthEventRef.current = {
      ts: Date.now(),
      kind,
      lastRequest: readLastAuthFetchRequest(),
    };

    if (!IS_PROD && !IS_TEST) {
      // eslint-disable-next-line no-console
      console.info('[auth]', kind, {
        ts: lastAuthEventRef.current.ts,
        lastRequest: lastAuthEventRef.current.lastRequest,
        authEpoch: authEpochRef.current,
      });
    }
  }, []);

  React.useEffect(() => {
    if (IS_PROD || IS_TEST || typeof window === 'undefined') {
      return;
    }

    const captureOnceIfRecent = (error: unknown) => {
      if (logoutDebugCapturedRef.current) {
        return;
      }
      const lastEvent = lastAuthEventRef.current;
      if (!lastEvent) {
        return;
      }
      const msSince = Date.now() - lastEvent.ts;
      if (msSince < 0 || msSince > 5000) {
        return;
      }

      logoutDebugCapturedRef.current = true;
      const asError = error instanceof Error ? error : null;

      // eslint-disable-next-line no-console
      console.error('[auth] post-auth-transition error captured', {
        kind: lastEvent.kind,
        msSince,
        lastRequest: lastEvent.lastRequest,
        message: asError?.message ?? String(error),
        stack: asError?.stack,
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      captureOnceIfRecent(event.reason);
    };

    const onError = (event: ErrorEvent) => {
      captureOnceIfRecent(event.error ?? event.message);
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
    };
  }, []);

  const setLoginRedirectReason = React.useCallback((reason: LoginRedirectReason | null) => {
    if (typeof window === 'undefined' || !window?.sessionStorage) {
      return;
    }
    try {
      if (reason) {
        window.sessionStorage.setItem(LOGIN_REDIRECT_REASON_STORAGE_KEY, reason);
      } else {
        window.sessionStorage.removeItem(LOGIN_REDIRECT_REASON_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures.
    }
  }, []);

  const clearLocalAuth = React.useCallback((reason: 'logout' | 'expired' | 'cross-tab') => {
    bumpAuthEpoch(reason);
    setUser(null);
    persistUser(null);
    setSessionToken('');
    clearAuthSessionToken();
    setIsProfileOpen(false);
  }, [bumpAuthEpoch]);

  const login = React.useCallback(async (identifier: string, password: string) => {
    const authEpochAtStart = authEpochRef.current;
    setIsAuthenticating(true);
    try {
      const loginEndpoint = `${apiBase}/api/users/login`;
      const response = await authFetch(loginEndpoint, {
        method: 'POST',
        body: JSON.stringify({ identifier, password, email: identifier }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          (payload && typeof payload.message === 'string' && payload.message.trim()) ||
          'Unable to log in. Please check your credentials.';
        throw new Error(message);
      }

      if (authEpochRef.current !== authEpochAtStart) {
        throw new Error('Login was interrupted. Please try again.');
      }

      const nextUser = (payload?.user ?? null) as AuthUser | null;
      if (!nextUser || typeof nextUser.id !== 'string') {
        throw new Error('Authentication response missing user data.');
      }

      const nextSessionToken = typeof payload?.session_token === 'string' ? payload.session_token : '';

      bumpAuthEpoch('login');
      logoutDebugCapturedRef.current = false;
      setUser(nextUser);
      persistUser(nextUser);
      setSessionToken(nextSessionToken);
      setAuthSessionToken(nextSessionToken);
      return nextUser;
    } catch (error) {
      const fallback = 'Unable to log in. Please try again.';
      if (error instanceof Error && error.message) {
        throw new Error(error.message);
      }
      throw new Error(fallback);
    } finally {
      setIsAuthenticating(false);
    }
  }, [apiBase, bumpAuthEpoch]);

  const logout = React.useCallback(() => {
    setLoginRedirectReason(null);
    clearLocalAuth('logout');
  }, [clearLocalAuth, setLoginRedirectReason]);

  const redirectToLogin = React.useCallback((targetPath?: string, options?: { reason?: LoginRedirectReason }) => {
    const from = targetPath ?? `${location.pathname}${location.search}${location.hash}`;
    navigate('/login', { replace: true, state: { ...(from ? { from } : null), ...(options?.reason ? { reason: options.reason } : null) } });
  }, [location.hash, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event) => {
      const currentToken = readSessionToken();
      if (!currentToken) {
        return;
      }
      const customEvent = event as CustomEvent<{ status?: number; url?: string; method?: string; reason?: string }>;
      if (customEvent?.detail?.status !== 401) {
        return;
      }

      setLoginRedirectReason('expired');
      clearLocalAuth('expired');
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, [clearLocalAuth, setLoginRedirectReason]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;
      if (event.key !== SESSION_STORAGE_KEY) return;

      const nextToken = (event.newValue || '').trim();
      if (nextToken) return; // Ignore logins/refreshes from other tabs.

      if (!sessionToken && !readSessionToken()) return;
      setLoginRedirectReason(null);
      clearLocalAuth('cross-tab');
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [clearLocalAuth, sessionToken, setLoginRedirectReason]);

  const updateUser = React.useCallback((updatedUser: AuthUser) => {
    setUser(updatedUser);
    persistUser(updatedUser);
  }, []);

  const openProfile = React.useCallback(() => {
    setIsProfileOpen(true);
  }, []);

  const closeProfile = React.useCallback(() => {
    setIsProfileOpen(false);
  }, []);

  const isAdmin = React.useMemo(() => (user?.role || '').toString().toLowerCase() === 'admin', [user?.role]);

  const getPresenceStatus = React.useCallback((): PresenceStatus => {
    if (!user) return 'available';
    const customFields = user.custom_fields as Record<string, unknown> | undefined;
    const status = customFields?.presence_status;
    if (status === 'available' || status === 'busy' || status === 'offline') {
      return status;
    }
    return 'available'; // default
  }, [user]);

  const updatePresenceStatus = React.useCallback(async (status: PresenceStatus): Promise<void> => {
    if (!user?.id) return;
    const authEpochAtStart = authEpochRef.current;

    try {
      const customFields = (user.custom_fields || {}) as Record<string, unknown>;
      const updatedCustomFields = {
        ...customFields,
        presence_status: status,
      };

      const payload: Record<string, unknown> = {
        custom_fields: updatedCustomFields,
      };

      const url = `${apiBase}/api/users/${user.id}`;
      const response = await authFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (authEpochRef.current !== authEpochAtStart) {
        return;
      }

      if (response.status === 0) {
        throw new Error('Network error: Unable to reach the server.');
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to update presence status: ${response.status} ${response.statusText || errorText}`);
      }

      const data = await response.json().catch(() => ({ status: 'error', message: 'Invalid JSON response' }));

      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to update presence status');
      }

      // Update local user data
      if (data.user) {
        const updatedUser: AuthUser = {
          ...user,
          ...data.user,
          custom_fields: updatedCustomFields,
        };
        if (authEpochRef.current === authEpochAtStart) {
          setUser(updatedUser);
          persistUser(updatedUser);
        }
      } else {
        // Update just the custom_fields if full user not returned
        const updatedUser: AuthUser = {
          ...user,
          custom_fields: updatedCustomFields,
        };
        if (authEpochRef.current === authEpochAtStart) {
          setUser(updatedUser);
          persistUser(updatedUser);
        }
      }
    } catch (err) {
      console.error('Failed to update presence status:', err);
      throw err;
    }
  }, [user, apiBase]);

  React.useEffect(() => {
    if (sessionToken) {
      setAuthSessionToken(sessionToken);
    }
  }, [sessionToken]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      sessionToken,
      isAdmin,
      isBootstrapping,
      isAuthenticating,
      isProfileOpen,
      login,
      logout,
      redirectToLogin,
      updateUser,
      openProfile,
      closeProfile,
      getPresenceStatus,
      updatePresenceStatus,
    }),
    [
      user,
      sessionToken,
      isAdmin,
      isBootstrapping,
      isAuthenticating,
      isProfileOpen,
      login,
      logout,
      redirectToLogin,
      updateUser,
      openProfile,
      closeProfile,
      getPresenceStatus,
      updatePresenceStatus,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
